/**
 * Error and Warning Detection Utilities
 * Migrated from React application
 */

import { ERROR_CODES, WARNING_CODES, THRESHOLDS, ErrorCode, WarningCode } from './error-warning-constants';
import { EVGData } from '../../core/models/dashboard-data.model';

/**
 * Analyzes EVG data for error and warning conditions
 * @param evgData - EVG data to analyze
 * @param evgNumber - EVG number (1-6)
 * @returns Object containing detected error and warning codes
 */
export function analyzeEVGData(evgData: EVGData, evgNumber: number): {
  errors: number[];
  warnings: number[];
} {
  const errors: number[] = [];
  const warnings: number[] = [];

  // Check for both lamps off (Error 120)
  if (!evgData.lamp1_status && !evgData.lamp2_status) {
    errors.push(120);
  }

  // Check for UZK too LOW (Error 140) - using voltage as proxy
  if (evgData.voltage < THRESHOLDS.voltage.min) {
    errors.push(140);
  }

  // Check for UZK too HIGH (Error 150)
  if (evgData.voltage > THRESHOLDS.voltage.max) {
    errors.push(150);
  }

  // Check for EVG over temp (Error 160)
  if (evgData.temperature > THRESHOLDS.temperature.max) {
    errors.push(160);
  }

  // Check for current overflow (Error 180)
  if (evgData.current > THRESHOLDS.current.max) {
    errors.push(180);
  }

  // Check for current critical (Error 190)
  if (evgData.current > THRESHOLDS.current.max * 1.2) {
    errors.push(190);
  }

  // Check for UZK drop warning (Warning 210)
  if (evgData.voltage < THRESHOLDS.voltage.min * 1.1) {
    warnings.push(210);
  }

  // Check for power supply low (Warning 220)
  if (evgData.voltage < THRESHOLDS.voltage.min * 1.05) {
    warnings.push(220);
  }

  // Check for bad lamp1 (Warning 240)
  if (!evgData.lamp1_status && evgData.lamp2_status) {
    warnings.push(240);
  }

  // Check for bad lamp2 (Warning 250)
  if (evgData.lamp1_status && !evgData.lamp2_status) {
    warnings.push(250);
  }

  return { errors, warnings };
}

/**
 * Get error code description
 */
export function getErrorDescription(code: number): string {
  return ERROR_CODES[code as ErrorCode] || 'Unknown Error';
}

/**
 * Get warning code description
 */
export function getWarningDescription(code: number): string {
  return WARNING_CODES[code as WarningCode] || 'Unknown Warning';
}

/**
 * Get color for error code
 */
export function getErrorColor(code: number): string {
  const errorColorMap: Record<number, string> = {
    120: '#ef4444',
    130: '#dc2626',
    140: '#b91c1c',
    150: '#991b1b',
    160: '#7f1d1d',
    170: '#dc2626',
    180: '#b91c1c',
    190: '#991b1b'
  };
  return errorColorMap[code] || '#ef4444';
}

/**
 * Get color for warning code
 */
export function getWarningColor(code: number): string {
  const warningColorMap: Record<number, string> = {
    210: '#f59e0b',
    220: '#d97706',
    230: '#b45309',
    240: '#92400e',
    250: '#78350f'
  };
  return warningColorMap[code] || '#f59e0b';
}

/**
 * Filter data by type (errors or warnings)
 */
export function filterByType(codes: number[], type: 'error' | 'warning'): number[] {
  if (type === 'error') {
    return codes.filter(code => code >= 120 && code <= 190);
  }
  return codes.filter(code => code >= 210 && code <= 250);
}

/**
 * Check if a code is an error
 */
export function isError(code: number): boolean {
  return code >= 120 && code <= 190;
}

/**
 * Check if a code is a warning
 */
export function isWarning(code: number): boolean {
  return code >= 210 && code <= 250;
}
